import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Phone, Loader2, Pencil, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import LanguageToggle from '@/components/shared/LanguageToggle';
import { useT } from '@/i18n';

/* Mandatory phone-number gate (approved plan, Sep 2026).
   Non-admin users without a saved mobile must enter one before using the
   app; users with an unconfirmed number confirm it once. Powers Telegram
   phone auto-linking. Non-dismissible by design. */
export default function PhoneGate({ children }) {
  const { user } = useAuth();
  const { t } = useT();
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState(null);
  const [mobile, setMobile] = useState('');
  const [saving, setSaving] = useState(false);

  const userId = user?.id;
  const userRole = user?.role;
  useEffect(() => {
    let alive = true;
    setStatus(null);
    setMode(null);
    if (!userId || userRole === 'admin') return undefined;
    axios.get(`${API}/auth/phone-status`)
      .then(r => { if (alive) { setStatus(r.data); setMode(r.data.mode); } })
      .catch(() => { if (alive) setStatus({ required: false }); });
    return () => { alive = false; };
  }, [userId, userRole]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/phone`, { mobile });
      toast.success(t('Number saved ✅'));
      setStatus({ required: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Could not save — please try again'));
    } finally {
      setSaving(false);
    }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/phone/confirm`);
      setStatus({ required: false });
    } catch (e) {
      toast.error(t('Try again'));
    } finally {
      setSaving(false);
    }
  };

  const blocked = user && user.role !== 'admin' && status?.required;

  return (
    <>
      {children}
      {blocked && (
        <div className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="phone-gate">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900">
                  {mode === 'confirm' ? t('Confirm your mobile number') : t('Enter your mobile number')}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {t('Needed to auto-join your Telegram groups')}
                </p>
              </div>
              <LanguageToggle />
            </div>

            {mode === 'confirm' ? (
              <div className="space-y-3">
                <p className="text-center text-2xl font-bold tracking-wider text-slate-800"
                  data-testid="phone-gate-current">{status.mobile}</p>
                <p className="text-sm text-center text-slate-600">{t('Is this your number?')}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => { setMode('collect'); setMobile(''); }}
                    data-testid="phone-gate-edit">
                    <Pencil className="w-4 h-4 mr-1" /> {t('Change')}
                  </Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={confirm}
                    disabled={saving} data-testid="phone-gate-confirm">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> {t('Yes, this is it')}</>}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Input inputMode="numeric" autoFocus placeholder={t('10-digit mobile number')}
                  value={mobile}
                  onChange={e => setMobile(e.target.value.replace(/[^\d+ ]/g, '').slice(0, 15))}
                  onKeyDown={e => e.key === 'Enter' && !saving && save()}
                  className="text-center text-lg tracking-widest h-11"
                  data-testid="phone-gate-input" />
                <Button className="w-full h-11 bg-indigo-600 hover:bg-indigo-700"
                  onClick={save} disabled={saving || mobile.replace(/\D/g, '').length < 10}
                  data-testid="phone-gate-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('Save')}
                </Button>
                <p className="text-[11px] text-center text-slate-400">
                  {t("The app won't continue until you save your number")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
